import { useMemo, type ReactNode } from "react";
import { KpiBadge } from "@/components/design/KpiBadge";
import { useListingRankingApplicationStates } from "@/pages/share/listings/use-listing-ranking-application-states";
import { OVERTAKING_STATE_LABELS } from "./auto-match/auto-match-runs";
import { CompanyRankingPanel } from "./CompanyRankingPanel";
import { buildCompanyRanking } from "./company-ranking-derive";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { MatchResultDetailDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { CandidateRankingPanel, useTeamResultView, type ResultTeamLike } from "./ResultPanel";
import { normaliseResultTeams } from "./normalise-result-teams";
import { parseDesign } from "./design-codec";
import { useAutoAddedMarks } from "./auto-match/use-auto-added-marks";
import { OrganizationalRecordBadge } from "./OrganizationalRecordBadge";
import type { MatchResultDetailNode } from "./use-match-editor";

export function SavedCandidateRanking({ matchId, resultId, listingId }: { matchId: string; resultId: string; listingId?: string }) {
  const intl = useIntl();
  const { states, unavailable } = useListingRankingApplicationStates(listingId);
  const applicationBadges = useMemo(() => new Map<string, ReactNode>([...states].map(([id, state]) =>
    [id, state ? <KpiBadge>{intl.formatMessage(OVERTAKING_STATE_LABELS[state])}</KpiBadge> : null])), [states, intl]);
  const statusError = unavailable ? <p role="status" className="smycles-field-meta">
    <FormattedMessage id="share.listings.ranking.applicationStatusUnavailable" /></p> : null;
  const query = useQuery(MatchResultDetailDocument, {
    variables: { id: resultId }, fetchPolicy: "cache-and-network", pollInterval: 20_000,
  });
  if (query.error) return <p role="alert">{userErrorMessage(intl, query.error)}</p>;
  if (query.loading && !query.data) return <p aria-busy="true"><FormattedMessage id="common.loading" /></p>;
  const result = query.data?.matchResult;
  const team = result ? normaliseResultTeams(result.teams)[0] : null;
  if (result?.companies?.length) {
    const ranked = buildCompanyRanking(result.companies.map(row => ({
      ...row,
      company: row.company ? {
        id: row.company.id, name: row.company.name, inactive: row.company.status === "INACTIVE",
        industryId: row.company.industry?.id ?? null, employeeCount: row.company.employeeCount ?? null,
      } : null,
    })));
    return <div className="space-y-4">
      {statusError}
      {result.usesOrganizationalRecord ? <OrganizationalRecordBadge /> : null}
      <CompanyRankingPanel ranked={ranked} applicationBadges={applicationBadges}
        showHistoricalValueNote={ranked.some(row => row.criteria.some(criterion => !criterion.actualRecorded))} />
    </div>;
  }
  if (!result || !team) return <p><FormattedMessage id="share.listings.ranking.unavailable" /></p>;
  return <>{statusError}<Ranking matchId={matchId} result={result} team={team} applicationBadges={applicationBadges} /></>;
}

function Ranking({ matchId, result, team, applicationBadges }: { matchId: string; result: MatchResultDetailNode; team: ResultTeamLike; applicationBadges: ReadonlyMap<string, ReactNode> }) {
  const view = useTeamResultView(team);
  const design = parseDesign(result.design);
  const marks = useAutoAddedMarks(matchId, team.autoAdded ?? []);
  return (
    <div className="space-y-4">
      {result.usesOrganizationalRecord ? <OrganizationalRecordBadge /> : null}
      <CandidateRankingPanel view={view} persons={team.persons}
        elementWeights={design.teamProfile?.elementWeights}
        requirementLeniency={design.requirementLeniency ?? "LENIENT"} autoAdded={marks} applicationBadges={applicationBadges} />
    </div>
  );
}
